import { useMutation, useQueryClient } from '@tanstack/react-query';

import { importCompaniesFromCSV } from '../../../api/csvImportApi';
import { prospectQueryKeys } from '../../../models/prospectQueries/keys';

/**
 * Hook for importing companies from CSV
 * @param {Object} callbacks - Optional callbacks
 * @param {Function} callbacks.onSuccess - Called on successful import
 * @param {Function} callbacks.onError - Called on error
 * @returns {Object} Mutation object
 */
export function useCSVImport(callbacks) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ queryName, columnMapping, companies }) =>
			importCompaniesFromCSV(queryName, columnMapping, companies),

		onSuccess: async (response, variables) => {
			// Optimistic update: Add new query to list
			const queryKey = prospectQueryKeys.filtered([
				'b2bheroLeadSearch',
				'b2bheroCommercialRegisterSearch',
				'csvImport',
			]);
			queryClient.setQueryData(queryKey, (oldData) => {
				if (!oldData?.data) return oldData;

				const newQuery = {
					id: response.prospectQueryId,
					queryName: variables.queryName,
					createdAt: new Date().toISOString(),
					input: {
						source: 'csvImport',
						columnMapping: variables.columnMapping,
					},
					queryTypes: {
						code: 'csvImport',
					},
					companyCount: response.imported,
				};

				return {
					...oldData,
					data: [newQuery, ...oldData.data],
					totalCount: (oldData.totalCount || 0) + 1,
				};
			});

			// Invalidate queries to fetch real data
			await queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });

			// Call user callback
			callbacks?.onSuccess?.(response);
		},

		onError: (error) => {
			callbacks?.onError?.(error);
		},
	});
}
